import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../lib/supabase";

interface Props { workspaceId: string; }

interface StudentKPI {
  userId: string;
  displayName: string;
  email: string;
  capital: number;
  reputation: number;
  taskCount: number;
  goals: { id: string; title: string; target_value: number; current_value: number; unit: string; category: string }[];
}

function getRankName(rep: number) {
  if (rep >= 81) return "부장"; if (rep >= 61) return "과장";
  if (rep >= 41) return "대리"; if (rep >= 21) return "사원"; return "인턴";
}
function getRankColor(rep: number) {
  if (rep >= 81) return "#EAB308"; if (rep >= 61) return "#E8375F";
  if (rep >= 41) return "#FF9F43"; if (rep >= 21) return "#7A9BFF"; return "#8B91A8";
}

export default function KPIManagerPanel({ workspaceId }: Props) {
  const [students, setStudents] = useState<StudentKPI[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<StudentKPI | null>(null);

  const fetchData = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    const { data: members } = await supabase.from("workspace_members").select("user_id").eq("workspace_id", workspaceId);
    if (!members?.length) { setLoading(false); return; }
    const uids = members.map((m: { user_id: string }) => m.user_id);

    const [{ data: profiles }, { data: states }, { data: tasks }, { data: goals }] = await Promise.all([
      supabase.from("profiles").select("id,display_name,email").in("id", uids),
      supabase.from("company_states").select("user_id,capital,reputation").eq("workspace_id", workspaceId),
      supabase.from("task_completions").select("user_id").eq("workspace_id", workspaceId),
      supabase.from("kpi_goals").select("*").eq("workspace_id", workspaceId).in("user_id", uids),
    ]);

    const profMap: Record<string, { display_name: string; email: string }> = {};
    (profiles ?? []).forEach((p: { id: string; display_name: string; email: string }) => { profMap[p.id] = p; });

    const stateMap: Record<string, { capital: number; reputation: number }> = {};
    (states ?? []).forEach((s: { user_id: string; capital: number; reputation: number }) => { stateMap[s.user_id] = s; });

    const taskCntMap: Record<string, number> = {};
    (tasks ?? []).forEach((t: { user_id: string }) => { taskCntMap[t.user_id] = (taskCntMap[t.user_id] ?? 0) + 1; });

    const goalsMap: Record<string, StudentKPI["goals"]> = {};
    (goals ?? []).forEach((g: StudentKPI["goals"][0] & { user_id: string }) => {
      if (!goalsMap[g.user_id]) goalsMap[g.user_id] = [];
      goalsMap[g.user_id].push(g);
    });

    setStudents(uids.map(uid => ({
      userId: uid,
      displayName: profMap[uid]?.display_name ?? "익명",
      email: profMap[uid]?.email ?? "-",
      capital: stateMap[uid]?.capital ?? 0,
      reputation: stateMap[uid]?.reputation ?? 0,
      taskCount: taskCntMap[uid] ?? 0,
      goals: goalsMap[uid] ?? [],
    })));
    setLoading(false);
  }, [workspaceId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="space-y-3">
      {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white/[0.02] rounded-xl animate-pulse border border-white/[0.04]" />)}
    </div>
  );

  if (!students.length) return (
    <div className="flex flex-col items-center justify-center py-20">
      <i className="ri-target-line text-3xl text-[#222222] block mb-3"></i>
      <p className="text-[#3D3D3D] text-sm">입장한 학생이 없어요</p>
    </div>
  );

  return (
    <div className="flex gap-5" style={{ minHeight: 600 }}>
      {/* 학생 목록 */}
      <div className="flex flex-col shrink-0 space-y-2" style={{ width: 340 }}>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[#555555] text-xs font-semibold">학생별 KPI 현황</div>
          <button type="button" onClick={fetchData} className="w-6 h-6 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
            <i className="ri-refresh-line text-xs"></i>
          </button>
        </div>
        {[...students].sort((a, b) => b.reputation - a.reputation).map(s => {
          const sel = selected?.userId === s.userId;
          const customGoalsDone = s.goals.filter(g => g.current_value >= g.target_value).length;
          return (
            <button key={s.userId} type="button" onClick={() => setSelected(s)}
              className={`w-full text-left px-4 py-3 rounded-xl border transition-all cursor-pointer ${sel ? "border-[#E8375F]/30 bg-[#E8375F]/[0.04]" : "border-white/[0.05] bg-[#111112] hover:border-white/[0.1]"}`}>
              <div className="flex items-center gap-2.5 mb-2">
                <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0"
                  style={{ backgroundColor: `${getRankColor(s.reputation)}15`, color: getRankColor(s.reputation) }}>
                  {s.displayName[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#CCCCCC] text-xs font-medium truncate">{s.displayName}</p>
                  <p className="text-[#3D3D3D] text-[9px]">{getRankName(s.reputation)} · {s.reputation}pt</p>
                </div>
                {s.goals.length > 0 && (
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#E8375F]/10 text-[#E8375F] border border-[#E8375F]/20 shrink-0">
                    {customGoalsDone}/{s.goals.length}
                  </span>
                )}
              </div>
              {/* 3대 지표 */}
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "자본금", value: `${s.capital.toLocaleString()}만`, target: 10000, current: s.capital, color: "#00C896" },
                  { label: "직급점수", value: `${s.reputation}pt`, target: 100, current: s.reputation, color: "#FF9F43" },
                  { label: "태스크", value: `${s.taskCount}건`, target: 10, current: s.taskCount, color: "#7A9BFF" },
                ].map(m => (
                  <div key={m.label}>
                    <div className="text-[#3D3D3D] text-[8px] mb-0.5">{m.label}</div>
                    <div className="text-[10px] font-bold tabular-nums" style={{ color: m.color }}>{m.value}</div>
                    <div className="w-full h-0.5 bg-white/[0.05] rounded-full overflow-hidden mt-1">
                      <div className="h-full rounded-full" style={{ width: `${Math.min(100, (m.current / m.target) * 100)}%`, backgroundColor: m.color }} />
                    </div>
                  </div>
                ))}
              </div>
            </button>
          );
        })}
      </div>

      {/* 상세 KPI */}
      <div className="flex-1 bg-[#111112] rounded-xl border border-white/[0.05] flex flex-col overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3">
            <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/[0.03] border border-white/[0.05]">
              <i className="ri-target-line text-2xl text-[#333333]"></i>
            </div>
            <p className="text-[#555555] text-sm">학생을 선택해 KPI를 확인하세요</p>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-white/[0.05] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: `${getRankColor(selected.reputation)}15`, color: getRankColor(selected.reputation) }}>
                  {selected.displayName[0]?.toUpperCase()}
                </div>
                <div>
                  <p className="text-[#F2F2F2] text-sm font-semibold">{selected.displayName}</p>
                  <p className="text-[#3D3D3D] text-[10px]">{getRankName(selected.reputation)} · {selected.reputation}pt</p>
                </div>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center text-[#3D3D3D] hover:text-[#777777] cursor-pointer transition-colors">
                <i className="ri-close-line text-base"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
              {/* 자동 추적 3대 지표 */}
              <div>
                <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest mb-3">자동 추적 지표</div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { label: "자본금", current: selected.capital, target: 10000, unit: "만원", color: "#00C896", icon: "ri-money-dollar-circle-line" },
                    { label: "직급점수", current: selected.reputation, target: 100, unit: "pt", color: "#FF9F43", icon: "ri-award-line" },
                    { label: "태스크 완료", current: selected.taskCount, target: 10, unit: "건", color: "#7A9BFF", icon: "ri-checkbox-line" },
                  ].map(m => {
                    const pct = Math.min(100, (m.current / m.target) * 100);
                    return (
                      <div key={m.label} className="bg-[#0C0C0E] rounded-xl border border-white/[0.05] p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <i className={`${m.icon} text-sm`} style={{ color: m.color }}></i>
                          {pct >= 100 && <i className="ri-checkbox-circle-fill text-emerald-400 text-xs"></i>}
                        </div>
                        <div className="text-[#666666] text-[10px] mb-1">{m.label}</div>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-sm font-bold tabular-nums" style={{ color: m.color }}>{m.current.toLocaleString()}</span>
                          <span className="text-[#333333] text-[9px]">/{m.target.toLocaleString()}{m.unit}</span>
                        </div>
                        <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: pct >= 100 ? "#00C896" : m.color }} />
                        </div>
                        <div className="text-[#333333] text-[9px] mt-1 text-right">{Math.round(pct)}%</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 사용자 정의 목표 */}
              <div>
                <div className="text-[#3D3D3D] text-[9px] uppercase tracking-widest mb-3">사용자 정의 목표 ({selected.goals.length}개)</div>
                {selected.goals.length === 0 ? (
                  <p className="text-[#333333] text-xs text-center py-6">학생이 아직 사용자 목표를 추가하지 않았어요</p>
                ) : (
                  <div className="space-y-2">
                    {selected.goals.map(g => {
                      const pct = Math.min(100, g.target_value > 0 ? (g.current_value / g.target_value) * 100 : 0);
                      const done = pct >= 100;
                      return (
                        <div key={g.id} className="bg-[#0C0C0E] rounded-lg border border-white/[0.05] px-3 py-2.5">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-[#AAAAAA] text-xs font-medium">{g.title}</span>
                            <span className="text-[10px] tabular-nums" style={{ color: done ? "#00C896" : "#E8375F" }}>
                              {g.current_value.toLocaleString()}/{g.target_value.toLocaleString()}{g.unit}
                            </span>
                          </div>
                          <div className="w-full h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: done ? "#00C896" : "#E8375F" }} />
                          </div>
                          <div className="text-[#333333] text-[9px] mt-1 text-right">{Math.round(pct)}%</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
